/*
 * Copyright (c) 2025 Étienne FOLIO
 * Tous droits réservés.
 *
 * Ce fichier fait partie d'un logiciel propriétaire.
 * Toute reproduction, modification ou distribution, totale ou partielle,
 * de ce fichier est interdite sans l'autorisation écrite préalable
 * de l'auteur.
 */

const BOOTSWATCH_VERSION = '5.3.8';
const DEFAULT_BOOTSWATCH_THEME = 'darkly';
const BOOTSWATCH_THEME_ATTRIBUTE = 'data-bootswatch-theme';
const THEME_STORAGE_KEY = 'fser-repport.theme';

export const getBootswatchCdnUrl = (
  themeName = DEFAULT_BOOTSWATCH_THEME,
  version = BOOTSWATCH_VERSION,
) =>
  `https://cdn.jsdelivr.net/npm/bootswatch@${version}/dist/${themeName}/bootstrap.min.css`;

export const BOOTSTRAP_THEME_URL = getBootswatchCdnUrl();

const getSafeWindow = () => (typeof window === 'undefined' ? null : window);

const normalizeThemeName = (value) => {
  if (typeof value !== 'string') {
    return DEFAULT_BOOTSWATCH_THEME;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed || DEFAULT_BOOTSWATCH_THEME;
};

const getStoredThemeConfig = () => {
  const safeWindow = getSafeWindow();
  if (!safeWindow) {
    return null;
  }

  try {
    const rawValue = safeWindow.localStorage.getItem(THEME_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }
    const parsed = JSON.parse(rawValue);
    if (parsed && typeof parsed === 'object') {
      const name = normalizeThemeName(parsed.name);
      const url = typeof parsed.url === 'string' ? parsed.url : getBootswatchCdnUrl(name);
      const label = typeof parsed.label === 'string' ? parsed.label : undefined;
      return label ? { name, url, label } : { name, url };
    }
  } catch (error) {
    // Ignore JSON or storage access errors and fall back to defaults.
  }

  return null;
};

const storeThemeConfig = (themeConfig) => {
  const safeWindow = getSafeWindow();
  if (!safeWindow) {
    return;
  }

  try {
    safeWindow.localStorage.setItem(
      THEME_STORAGE_KEY,
      JSON.stringify(themeConfig),
    );
  } catch (error) {
    // Ignore storage write errors (private mode, quota, etc.).
  }
};

export const ensureBootstrapThemeLoaded = (themeUrl = BOOTSTRAP_THEME_URL) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) {
    return null;
  }

  const existingLink = head.querySelector(
    `link[rel="stylesheet"][${BOOTSWATCH_THEME_ATTRIBUTE}]`,
  );

  if (existingLink) {
    const currentHref = existingLink.getAttribute('href');
    if (currentHref === themeUrl) {
      return existingLink;
    }
    existingLink.setAttribute('href', themeUrl);
    existingLink.setAttribute(BOOTSWATCH_THEME_ATTRIBUTE, themeUrl);
    return existingLink;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = themeUrl;
  link.setAttribute(BOOTSWATCH_THEME_ATTRIBUTE, themeUrl);
  head.appendChild(link);
  return link;
};

const buildThemeConfig = ({ name, url, label } = {}) => {
  const themeName = normalizeThemeName(name);
  const themeUrl = url || getBootswatchCdnUrl(themeName);
  const themeLabel = typeof label === 'string' && label.trim() ? label.trim() : themeName;
  return { name: themeName, url: themeUrl, label: themeLabel };
};

export const applyThemeConfig = (themeConfig) => {
  const config = buildThemeConfig(themeConfig);
  ensureBootstrapThemeLoaded(config.url);
  storeThemeConfig(config);
  return config;
};

export const loadStoredTheme = () => {
  const stored = getStoredThemeConfig();
  return applyThemeConfig(stored || undefined);
};

export const getActiveThemeConfig = () => {
  const stored = getStoredThemeConfig();
  return stored ? buildThemeConfig(stored) : buildThemeConfig();
};

export const getBootswatchThemeConfig = () => ({
  version: BOOTSWATCH_VERSION,
  defaultTheme: DEFAULT_BOOTSWATCH_THEME,
});
